/**
 * Reusable Carcass Finish Fields Component
 * Used in both:
 * - Project Wizard (Config3Finitions)
 * - Element Forms (ModuleBasForm, etc.)
 */

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Combobox } from '@/components/ui/combobox'

interface CarcassFinishFieldsProps {
  // Values
  finish: string
  color: string
  edgeColor?: string
  edgeColorSameAsDoor: boolean
  sideViewFinish?: string
  sideViewFinishSameAsDoor: boolean
  
  // Callbacks
  onFinishChange: (value: string) => void
  onColorChange: (value: string) => void
  onEdgeColorChange: (value: string) => void
  onEdgeColorSameChange: (same: boolean) => void
  onSideViewFinishChange: (value: string) => void
  onSideViewFinishSameChange: (same: boolean) => void
  
  // Options
  materialOptions?: string[]
  
  // Errors (optional)
  errors?: {
    finish?: string
    color?: string
  }
}

export function CarcassFinishFields({
  finish,
  color,
  edgeColor = '',
  edgeColorSameAsDoor,
  sideViewFinish = '',
  sideViewFinishSameAsDoor,
  onFinishChange,
  onColorChange,
  onEdgeColorChange,
  onEdgeColorSameChange,
  onSideViewFinishChange,
  onSideViewFinishSameChange,
  materialOptions = ['Mélamine', 'Stratifié', 'MDF', 'Bois massif', 'Autre'],
  errors,
}: CarcassFinishFieldsProps) {
  return (
    <div className="space-y-4">
      {/* Material/Finish */}
      <div className="space-y-2">
        <Label>Finition de caisse *</Label>
        <Combobox
          options={materialOptions.map((material) => ({
            value: material,
            label: material,
          }))}
          value={finish}
          onChange={onFinishChange}
          placeholder="Rechercher ou saisir une finition..."
          searchPlaceholder="Rechercher..."
          emptyText="Aucune finition trouvée."
          allowCustomValue={true}
          className={errors?.finish ? 'border-red-500' : ''}
        />
        {errors?.finish && (
          <p className="text-sm text-red-600">{errors.finish}</p>
        )}
      </div>

      {/* Color */}
      <div className="space-y-2">
        <Label>Coloris caisse *</Label>
        <Input
          value={color}
          onChange={(e) => onColorChange(e.target.value)}
          placeholder="Ex: Blanc, Gris clair..."
          className={errors?.color ? 'border-red-500' : ''}
        />
        {errors?.color && (
          <p className="text-sm text-red-600">{errors.color}</p>
        )}
      </div>

      {/* Edge Color */}
      <div className="space-y-2">
        <Label>Coloris du chant (rebord)</Label>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={edgeColorSameAsDoor}
              onChange={() => onEdgeColorSameChange(true)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm">Identique aux portes</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={!edgeColorSameAsDoor}
              onChange={() => onEdgeColorSameChange(false)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm">Différent :</span>
            {!edgeColorSameAsDoor && (
              <Input
                value={edgeColor}
                onChange={(e) => onEdgeColorChange(e.target.value)}
                placeholder="Coloris"
                className="flex-1"
              />
            )}
          </label>
        </div>
      </div>

      {/* Side View Finish */}
      <div className="space-y-2">
        <Label>Côté vue (finition côté visible)</Label>
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={sideViewFinishSameAsDoor}
              onChange={() => onSideViewFinishSameChange(true)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm">Identique aux portes</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={!sideViewFinishSameAsDoor}
              onChange={() => onSideViewFinishSameChange(false)}
              className="w-4 h-4 cursor-pointer"
            />
            <span className="text-sm">Différent :</span>
            {!sideViewFinishSameAsDoor && (
              <Input
                value={sideViewFinish}
                onChange={(e) => onSideViewFinishChange(e.target.value)}
                placeholder="Finition"
                className="flex-1"
              />
            )}
          </label>
        </div>
      </div>
    </div>
  )
}
